import { PassMetadata, PassMetadataField } from "../models/PassMetadata";
import moment from "moment";
import PassTypeService from "./PassTypeService";
import R1STranslator from "./R1STranslator";

//Converts R1S site and pass type information into field structures that's used to build the pass input form
export default class PassMetadataService {
  getFieldsToUpdate(passDefinition, pass) {
    let translator = new R1STranslator();
    let fields = [];
    let passTypeService = new PassTypeService();
    let passType = passTypeService.getPassType(passDefinition, pass.passtype);
    let startdate = translator.calculateStartDate(passType.duration, pass);
    let enddate = translator.calculateEndDate(passDefinition, pass);
    fields.push({ name: "start-date", value: startdate });
    fields.push({ name: "enddate", value: enddate });
    let plateDefault = translator.getLicensePlateDefaults(passDefinition, pass);
    if (plateDefault !== "") {
      fields.push({ name: "licenseplate", value: plateDefault });
    }

    //Get price
    let price = translator.getPrice(pass, passDefinition);

    if (price) {
      fields.push({ name: "price", value: price.value });
      fields.push({ name: "priceDisplay", value: price.displayValue });
    }

    //Get sku
    let sku = translator.getSku(pass, passDefinition);
    fields.push({ name: "sku", value: sku });

    //Get description
    let description = translator.getDescription(pass, passDefinition);
    fields.push({ name: "description", value: description });

    //Clear day/night data if passtype changes from segment duration
    if (passType.duration != "segment" && (pass.days || pass.nights)) {
      fields.push({ name: "days", value: null });
      fields.push({ name: "nights", value: null });
    }

    return fields;
  }

  getPassMetadataForCategory(parkDefinition, passDefinition, pass, category) {
    let passMetadata = this.getPassMetadata(
      parkDefinition,
      passDefinition,
      pass
    );
    if (category) {
      return passMetadata.filterFieldsByCategory(category);
    }
    return passMetadata;
  }

  getPassMetadata(parkDefinition, passDefinition, pass) {
    let translator = new R1STranslator();
    let passMetadata = new PassMetadata();
    passMetadata.rows = [];
    let siteInfo = {
      id: parkDefinition.id,
      name: parkDefinition.name,
      agency: parkDefinition.agency,
      state: parkDefinition.state,
      blackout_periods: parkDefinition.blackout_periods,
      free_days: parkDefinition.free_days,
      contents: parkDefinition.contents
    };

    passMetadata.siteInfo = siteInfo;

    //Fields that apply to all passes
    let defaultFields = this.getDefaultFields(passDefinition);
    passMetadata.addFields(defaultFields);

    let passTypeDefined = false;
    if (pass && pass.hasOwnProperty("passtype") && pass.passtype !== "") {
      passTypeDefined = true;
    }
    let field = new PassMetadataField();
    if (passTypeDefined) {
      let passTypeService = new PassTypeService();
      let passType = passTypeService.getPassType(passDefinition, pass.passtype);

      //Segment uses different field labels for start/end dates
      if (passType.duration === "segment") {
        let dayNightFields = this.getDayNightFields(passType);
        passMetadata.addFields(dayNightFields);
      } else {
        field = new PassMetadataField();
        field.id = "2";
        field.name = "start-date";
        field.label = "Start Date";
        field.dataType = "Date";
        field.inputType = "Text";
        field.isDisplayed = passTypeDefined;
        field.isRequired = true;
        if (passType.duration === "annual" || passType.duration === "season") {
          field.isDisabled = true;
        } else {
          field.isDisabled = false;
        }
        field.row = 2;
        field.defaultValue = moment().format("MM/DD/YYYY");
        field.category = "passinfo";
        passMetadata.addField(field);

        field = new PassMetadataField();
        field.id = "3";
        field.name = "enddate";
        field.label = "Valid Through";
        field.dataType = "Date";
        field.inputType = "Text";
        field.isDisplayed = passTypeDefined;
        field.isRequired = true;
        field.isDisabled = true;
        field.row = 2;
        field.defaultValue = translator.calculateEndDate(passDefinition, pass);
        field.category = "passinfo";
        passMetadata.addField(field);
      }

      //Show discount prompt
      if (passType.features.discount) {
        passMetadata.showDiscount = true;
      }

      //Check if vehicle type/plate is needed
      let vehicleFields = this.getVehicleFields(passType, pass.vehicle);
      passMetadata.addFields(vehicleFields);

      //Check attendee info
      let attendeeFields = this.getAttendeeFields(passType);
      passMetadata.addFields(attendeeFields);

      //Check if watercraft type/plate is needed
      let watercraftFields = this.getWatercraftFields(
        passType,
        pass.watercraft
      );
      passMetadata.addFields(watercraftFields);

      //Get any option-select fields
      let optionFields = this.getDropdownFields(passType);
      passMetadata.addFields(optionFields);

      //Check day/night
      // let dayNightFields = this.getDayNightFields(passtype, pass);
      // passMetadata.addFields(dayNightFields);

      let price = translator.getPrice(pass, passDefinition);

      if (price) {
        pass.price = price.value;
        pass.priceDisplay = price.displayValue;
      }
    }

    return passMetadata;
  }

  getDefaultFields(passDefinition) {
    let fields = [];

    //Feilds always present but not in pass definition
    //Visitor Info Fields
    field = new PassMetadataField();
    field.id = "8";
    field.name = "firstname";
    field.label = "First Name";
    field.dataType = "String";
    field.inputType = "Text";
    field.isDisplayed = true;
    field.isRequired = true;
    field.isDisabled = false;
    field.row = 1;
    field.defaultValue = "";
    field.category = "visitorinfo";

    fields.push(field);

    field = new PassMetadataField();
    field.id = "9";
    field.name = "lastname";
    field.label = "Last Name";
    field.dataType = "String";
    field.inputType = "Text";
    field.isDisplayed = true;
    field.isRequired = true;
    field.isDisabled = false;
    field.row = 1;
    field.defaultValue = "";
    field.category = "visitorinfo";

    fields.push(field);

    field = new PassMetadataField();
    field.id = "10";
    field.name = "visitoremail";
    field.label = "Visitor Email";
    field.dataType = "Email";
    field.inputType = "Text";
    field.isDisplayed = true;
    field.isRequired = true;
    field.isDisabled = false;
    field.row = 2;
    field.defaultValue = "";
    field.category = "visitorinfo";
    field.validationRules = "required|email";

    fields.push(field);

    field = new PassMetadataField();
    field.id = "11";
    field.name = "visitoremailconfirm";
    field.label = "Re-Enter Visitor Email";
    field.dataType = "Email";
    field.inputType = "Text";
    field.isDisplayed = true;
    field.isRequired = true;
    field.isDisabled = false;
    field.row = 3;
    field.defaultValue = "";
    field.category = "visitorinfo";
    field.validationRules = "required";

    fields.push(field);

    let field = new PassMetadataField();
    field.id = "1";
    field.name = "passtype";
    field.label = "Pass Type";
    field.dataType = "String";
    field.inputType = "DropDownList";
    field.isDisplayed = true;
    field.isRequired = true;
    field.isDisabled = false;
    field.options = [];

    //Grab pass type options
    passDefinition.pass_types.forEach(passType => {
      let label = passType.name;
      let option = {
        name: passType.id,
        label: label
      };
      if (
        passType.contents &&
        passType.contents.description &&
        passType.contents.description[0].paragraphs
      ) {
        option.description = passType.contents.description[0].paragraphs[0];
      }
      field.options.push(option);
      //}
    });

    field.row = 1;
    field.column = 1;
    field.category = "passinfo";
    field.defaultValue = "";
    fields.push(field);

    return fields;
  }

  getDayNightFields(passType) {
    let fields = [];
    if (!passType || passType.duration !== "segment") {
      return;
    }
    let field = new PassMetadataField();
    field.id = "2";
    field.name = "start-date";
    field.label = "Arrival Date";
    field.dataType = "Date";
    field.inputType = "Text";
    field.isDisplayed = true;
    field.isRequired = true;
    field.isDisabled = false;
    field.row = 2;
    field.defaultValue = moment().format("MM/DD/YYYY");
    field.category = "passinfo";
    fields.push(field);

    field = new PassMetadataField();
    field.id = "3";
    field.name = "enddate";
    field.label = "Departure Date";
    field.dataType = "Date";
    field.inputType = "Text";
    field.isDisplayed = true;
    field.isRequired = true;
    field.isDisabled = false;
    field.row = 2;
    field.defaultValue = moment().format("MM/DD/YYYY");
    field.category = "passinfo";
    fields.push(field);

    field = new PassMetadataField();
    field.id = "4";
    field.name = "adults";
    field.label = "Number Of Adults (age 16 and over)";
    field.dataType = "Number";
    field.inputType = "Text";
    field.isDisplayed = true;
    field.isRequired = true;
    field.isDisabled = false;
    field.row = 2;
    field.defaultValue = 1;
    field.category = "passinfo";

    fields.push(field);
    return fields;
  }

  getAttendeeFields(passType) {
    let fields = [];
    if (
      !passType.features ||
      !passType.features["attendee-info"] ||
      (!passType.features["attendee-info"].adults &&
        !passType.features["attendee-info"].children)
    ) {
      return;
    }

    if (passType.features["attendee-info"].adults) {
      let field = new PassMetadataField();
      field.id = "a1";
      field.name = "adults";
      field.label = passType.features["attendee-info"].adults.description;
      field.dataType = "Number";
      field.inputType = "Text";
      field.isDisplayed = true;
      field.isRequired = true;
      field.isDisabled = false;
      field.row = 3;
      field.defaultValue = "";
      field.category = "passinfo";
      field.minValue = passType.features["attendee-info"].adults.attendee_min;
      field.maxValue = passType.features["attendee-info"].adults.attendee_max;
      fields.push(field);
    }

    if (passType.features["attendee-info"].children) {
      let field = new PassMetadataField();
      field.id = "a2";
      field.name = "children";
      field.label = passType.features["attendee-info"].children.description;
      field.dataType = "Number";
      field.inputType = "Text";
      field.isDisplayed = true;
      field.isRequired = true;
      field.isDisabled = false;
      field.row = 3;
      field.defaultValue = "0";
      field.category = "passinfo";
      field.minValue = passType.features["attendee-info"].children.attendee_min;
      field.maxValue = passType.features["attendee-info"].children.attendee_max;
      fields.push(field);
    }

    return fields;
  }

  getWatercraftFields(passType, watercraft) {
    let fields = [];
    if (
      !passType.features ||
      !passType.features["equipment-info"] ||
      !passType.features["equipment-info"].watercraft
    ) {
      return;
    }
    let field = new PassMetadataField();
    field.id = "w1";
    field.name = "watercraft";
    field.label = "Boat Type";
    field.dataType = "String";
    field.inputType = "DropDownList";
    field.isDisplayed = true;
    field.isRequired = true;
    field.isDisabled = false;
    field.row = 4;
    field.defaultValue = "";
    field.category = "passinfo";

    field.options = [
      {
        name: "motorized-boat",
        label:
          passType.features["equipment-info"].watercraft["motorized-boat"].name
      },
      {
        name: "no-boat",
        label: passType.features["equipment-info"].watercraft["no-boat"].name
      },
      {
        name: "non-motorized-boat",
        label:
          passType.features["equipment-info"].watercraft["non-motorized-boat"]
            .name
      },
      {
        name: "rental-boat",
        label:
          passType.features["equipment-info"].watercraft["rental-boat"].name
      }
    ];
    fields.push(field);

    if (
      (watercraft === "motorized-boat" &&
        passType.features["equipment-info"].watercraft["motorized-boat"]
          .license_required) ||
      (watercraft === "no-boat" &&
        passType.features["equipment-info"].watercraft["no-boat"]
          .license_required) ||
      (watercraft === "non-motorized-boat" &&
        passType.features["equipment-info"].watercraft["non-motorized-boat"]
          .license_required) ||
      (watercraft === "rental-boat" &&
        passType.features["equipment-info"].watercraft["rental-boat"]
          .license_required)
    ) {
      field = new PassMetadataField();
      field.id = "w2";
      field.name = "licenseplate";
      field.label = "Boat Registration Number";
      field.dataType = "String";
      field.inputType = "Text";
      field.isDisplayed = true;
      field.isRequired = true;
      field.row = 4;
      field.category = "passinfo";

      fields.push(field);
    }
    return fields;
  }

  getDropdownFields(passType) {
    let fields = [];
    if (
      !passType.features ||
      !passType.features["option-select"] ||
      !passType.features["option-select"][0]
    ) {
      return;
    }
    for (let i = 0; i < passType.features["option-select"].length; i++) {
      let option = passType.features["option-select"][i];

      let field = new PassMetadataField();
      field.id = "o" + i;
      field.name = "option:" + option.type;
      field.label = option.label;
      field.dataType = "String";
      field.inputType = "DropDownList";
      field.isDisplayed = true;
      field.isRequired = option.is_required;
      field.isDisabled = false;
      field.row = 5;
      field.defaultValue = "";
      field.category = "passinfo";

      field.options = [];
      for (let j = 0; j < option.values.length; j++) {
        let optionValue = option.values[j];
        let optionItem = {
          name: optionValue.value,
          label: optionValue.label
        };
        field.options.push(optionItem);
      }
      fields.push(field);
    }

    return fields;
  }

  getVehicleFields(passType, vehicle) {
    let fields = [];
    if (
      !passType.features ||
      !passType.features["equipment-info"] ||
      !passType.features["equipment-info"].vehicle
    ) {
      return;
    }

    let field = new PassMetadataField();
    field.id = "v1";
    field.name = "vehicle";
    field.label = "Vehicle Type";
    field.dataType = "String";
    field.inputType = "DropDownList";
    field.isDisplayed = true;
    field.isRequired = true;
    field.isDisabled = false;
    field.row = 4;
    field.defaultValue = "";
    field.category = "passinfo";

    field.options = [];
    let plateField = null;
    for (let property in passType.features["equipment-info"].vehicle) {
      if (
        passType.features["equipment-info"].vehicle.hasOwnProperty(property)
      ) {
        let option = {
          name: property,
          label: passType.features["equipment-info"].vehicle[property].name
        };
        field.options.push(option);

        if (
          passType.features["equipment-info"].vehicle[property]
            .license_required &&
          vehicle === property
        ) {
          plateField = {};
          plateField.id = "7";
          plateField.name = "licenseplate";
          plateField.label = "License Plate Number";
          plateField.dataType = "String";
          plateField.inputType = "Text";
          plateField.isDisplayed = true;
          plateField.isRequired = true;
          plateField.row = 4;
          plateField.category = "passinfo";
        }
      }
    }
    fields.push(field);
    if (plateField) {
      fields.push(plateField);
    }

    return fields;
  }
}
